import { useState, useEffect } from "react";
import { MessageSquare } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import API from "@/lib/api";
import { toast } from "sonner";

/**
 * Click-to-text wrapper. Opens a small composer where staff type the message,
 * then hands off to the OS `sms:` URI which the staff member's installed Vonage
 * Business app (or default SMS handler) intercepts and pre-fills.
 *
 * Logs the attempt + body to /api/messages/log-attempt for HIPAA audit.
 *
 * Props:
 *   - phone:        formatted phone string
 *   - patientId:    user id of the patient being texted
 *   - patientName:  optional, used in the dialog header
 *   - className:    optional extra classes
 */
export default function ClickToTextPhone({ phone, patientId, patientName = "", className = "" }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [stats, setStats] = useState({});
  const [templates, setTemplates] = useState([]);
  const [sending, setSending] = useState(false);

  // Fetch usage counts AND template list when dialog opens
  useEffect(() => {
    if (!open) return;
    Promise.all([
      API.get("/messages/template-stats").then(({ data }) => data || {}).catch(() => ({})),
      API.get("/sms-templates").then(({ data }) => Array.isArray(data) ? data : []).catch(() => []),
    ]).then(([s, t]) => {
      setStats(s);
      // Server returns active-only for non-admins; filter just in case.
      setTemplates(t.filter((x) => x.active !== false));
    });
  }, [open]);

  if (!phone) return null;
  const dialable = String(phone).replace(/[^\d+]/g, "");

  const send = async () => {
    if (!body.trim()) { toast.info("Type a message first."); return; }
    setSending(true);

    const smsUri = `sms:${dialable}?body=${encodeURIComponent(body.trim())}`;

    if (patientId) {
      try {
        await API.post("/messages/log-attempt", {
          patient_id: patientId,
          dialed_number: dialable,
          body: body.trim(),
          template_id: activeTemplate || "",
        });
      } catch {
        // Silent
      }
    }

    window.location.href = smsUri;
    setSending(false);
    setOpen(false);
    setBody("");
    setActiveTemplate(null);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={`Text ${phone}`}
        className={`inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700 hover:underline ${className}`}
        data-testid={`text-${patientId || dialable}`}
      >
        <MessageSquare className="w-3.5 h-3.5" /> Text
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="text-compose-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-emerald-600" />
              <span>Text {patientName || "patient"}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 text-xs">
              <p className="text-slate-500">Sending to</p>
              <p className="font-mono font-semibold text-slate-900">{phone}</p>
            </div>

            <div>
              <Label className="text-xs font-medium text-slate-600 mb-2 block">Quick templates</Label>
              {templates.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No templates configured. Type your message below.</p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {templates.map((t) => {
                    const count = stats[t.id] || 0;
                    const isActive = activeTemplate === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => { setBody(t.body); setActiveTemplate(t.id); }}
                        className={`text-xs px-3 py-2 rounded-xl border text-left transition-colors ${
                          isActive
                            ? "bg-emerald-600 text-white border-emerald-600"
                            : "bg-white hover:bg-slate-50 text-slate-700 border-slate-200"
                        }`}
                        data-testid={`text-template-${t.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate">{t.label}</span>
                          <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                            isActive
                              ? "bg-white/20 text-white"
                              : count > 0
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                                : "bg-slate-100 text-slate-400"
                          }`}>
                            {count}×
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="text-body" className="text-xs font-medium text-slate-600">Message</Label>
              <Textarea
                id="text-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Type your message…"
                className="mt-1 rounded-xl"
                rows={4}
                maxLength={1000}
                data-testid="text-body-input"
              />
              <p className="text-[11px] text-slate-400 mt-1">{body.length}/1000 · Saved to the patient&apos;s audit log. The Vonage app will open with this text pre-filled.</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="text-cancel">Cancel</Button>
            <Button
              onClick={send}
              disabled={sending || !body.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="text-send"
            >
              {sending ? "Opening Vonage…" : "Open in Vonage"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
